import React from 'react';
import ReportingPageLayout from './ReportingPageLayout';
import useReportingPage from '../hooks/useReportingPage';

// Only reports that actually have a built page + route appear here.
// Add an entry as each Memberships sub-report ships.
const REPORTS = [
  {
    key: 'memberships_current_members',
    title: 'Current Members',
    description: 'Everyone with a live membership right now, with plan, price, and renewal date.',
    path: (teamId) => `/teams/${teamId}/reporting/memberships/current-members`,
  },
  {
    key: 'memberships_pending_cancelations',
    title: 'Pending Cancelations',
    description: 'Members who are still active but have opted out, with the date their membership lapses.',
    path: (teamId) => `/teams/${teamId}/reporting/memberships/pending-cancelations`,
  },
  {
    key: 'memberships_lapsed',
    title: 'Lapsed Members',
    description: 'Former members whose membership has ended and who have not rejoined — a win-back list.',
    path: (teamId) => `/teams/${teamId}/reporting/memberships/lapsed`,
  },
  {
    key: 'memberships_upcoming_renewals',
    title: 'Upcoming Renewals',
    description: 'Members set to renew, soonest first, with the amount each renewal will charge.',
    path: (teamId) => `/teams/${teamId}/reporting/memberships/upcoming-renewals`,
  },
  {
    key: 'memberships_failed_payments',
    title: 'Failed Payments',
    description: 'Members whose renewal charge failed, with how long it has been failing and when they last paid.',
    path: (teamId) => `/teams/${teamId}/reporting/memberships/failed-payments`,
  },
  {
    key: 'memberships_revenue',
    title: 'Membership Revenue',
    description: 'Membership payments collected in a period, split into new memberships and renewals.',
    path: (teamId) => `/teams/${teamId}/reporting/memberships/revenue`,
  },
  {
    key: 'memberships_growth',
    title: 'Growth & Retention',
    description: 'Members gained and lost each period, net change, and the share of members you kept.',
    path: (teamId) => `/teams/${teamId}/reporting/memberships/growth`,
  },
  {
    key: 'memberships_engagement',
    title: 'Member Engagement',
    description: 'Events attended, tickets, add-ons, and donations before vs. after each member joined.',
    path: (teamId) => `/teams/${teamId}/reporting/memberships/engagement`,
  },
];

function ReportingMembershipsPage({ team }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage('year_to_date');

  const teamId = team?.id;

  return (
    <ReportingPageLayout
      team={team}
      activeKey="memberships"
      title="Memberships"
      subtitle="Membership and recurring revenue reports for your organization."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={[]}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      showDatePicker={false}
      showFilters={false}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        {REPORTS.map((report) => (
          <a
            key={report.key}
            href={teamId ? report.path(teamId) : '#'}
            className="text-decoration-none d-block"
            style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: 20, color: 'inherit' }}
          >
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--sl-text-primary, #101828)', marginBottom: 6 }}>
              {report.title}
            </div>
            <div style={{ fontSize: 13, color: 'var(--sl-text-quaternary, #667085)', lineHeight: 1.5 }}>
              {report.description}
            </div>
          </a>
        ))}
      </div>
    </ReportingPageLayout>
  );
}

export default ReportingMembershipsPage;
